import React, { useState, useEffect } from 'react';
import {
    Upload,
    Button,
    Form,
    Card,
    message,
    Typography,
    Space,
    Image,
    Radio,
    Progress,
    GetProp
} from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ApiService } from '../services/api';
import { Tag, SortCategory } from '../types';

const { Title } = Typography;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file as Blob);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

const UploadPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState<Tag[]>([]);
    const [sortCategories, setSortCategories] = useState<SortCategory[]>([]);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [uploadProgress, setUploadProgress] = useState<{ success: number; total: number } | null>(null);

    useEffect(() => {
        loadTags();
        loadSortCategories();
    }, []);

    const loadTags = async () => {
        try {
            const response = await ApiService.getTagList({ language: 'zh' });
            if (response.code === 200) {
                setTags(response.data.tags);
            }
        } catch (error: any) {
            message.error('获取标签列表失败');
        }
    };

    const loadSortCategories = async () => {
        try {
            const response = await ApiService.getSortCategoryList({ page: 1, page_size: 100 });
            if (response.code === 200) {
                // 按sort_index排序
                const sortedCategories = response.data.sort_categories.sort((a, b) => a.sort_index - b.sort_index);
                setSortCategories(sortedCategories);
            }
        } catch (error: any) {
            message.error('获取排序类别列表失败');
        }
    };

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as FileType);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const uploadProps: UploadProps = {
        name: 'images',
        multiple: true,
        listType: 'picture-card',
        fileList,
        beforeUpload: (file) => {
            const isImage = file.type?.startsWith('image/');
            if (!isImage) {
                message.error('只能上传图片文件!');
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error('图片大小不能超过 10MB!');
                return false;
            }

            return false; // 阻止自动上传
        },
        onPreview: handlePreview,
        onChange: ({ fileList: newFileList }) => {
            // 限制最多选择50张图片
            if (newFileList.length > 50) {
                message.warning('最多只能选择50张图片');
                return;
            }
            setFileList(newFileList);
        },
        onRemove: () => true
    };

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>上传图片</div>
        </button>
    );

    const onFinish = async (values: any) => {
        if (fileList.length === 0) {
            message.error('请选择要上传的图片');
            return;
        }

        setLoading(true);
        setUploadProgress({ success: 0, total: fileList.length });

        try {
            const formData = new FormData();

            // 添加所有图片文件
            fileList.forEach((file) => {
                formData.append('images', file.originFileObj as File);
            });

            // 添加其他参数
            formData.append('tag_id', values.tag_id.toString());
            formData.append('sort_category', values.sort_category.toString());
            formData.append('language_type', '0');
            // formData.append('designer', values.designer.toString());
            formData.append('designer', '0');

            const result = await ApiService.uploadImage(formData);

            if (result.code === 200) {
                const { success_count, failed_count, total_uploaded } = result.data;
                setUploadProgress({ success: success_count, total: total_uploaded });

                if (failed_count === 0) {
                    message.success(`成功上传 ${success_count} 张图片`);
                    form.resetFields();
                    setFileList([]);
                } else if (success_count > 0) {
                    message.warning(`成功上传 ${success_count} 张图片，失败 ${failed_count} 张`);

                    // 显示失败详情
                    const failedFiles = result.data.results.filter((r: any) => !r.success);
                    if (failedFiles.length > 0) {
                        console.log('上传失败的文件:', failedFiles);
                    }
                } else {
                    message.error('所有图片上传失败');
                }
            } else {
                message.error(result.message || '上传失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '上传失败，请检查网络连接');
        } finally {
            setLoading(false);
            setUploadProgress(null);
        }
    };

    return (
        <div>
            <Title level={2}>
                <UploadOutlined /> 图片上传
            </Title>

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    style={{ maxWidth: 800 }}
                >
                    <Form.Item label="选择图片">
                        <Upload {...uploadProps}>
                            {fileList.length >= 100 ? null : uploadButton}
                        </Upload>
                        {previewImage && (
                            <Image
                                wrapperStyle={{ display: 'none' }}
                                preview={{
                                    visible: previewOpen,
                                    onVisibleChange: (visible) => setPreviewOpen(visible),
                                    afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                }}
                                src={previewImage}
                            />
                        )}
                    </Form.Item>

                    <Form.Item
                        name="tag_id"
                        label="选择标签"
                        rules={[{ required: true, message: '请选择标签' }]}
                    >
                        <Radio.Group optionType="button" buttonStyle="solid" size="middle">
                            <Space wrap style={{ width: '100%' }}>
                                {tags.map(tag => (
                                    <Radio.Button key={tag.id} value={tag.id} style={{ marginBottom: 8 }}>
                                        {tag.tag} ({tag.remark})
                                    </Radio.Button>
                                ))}
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        name="sort_category"
                        label="排序类别"
                        rules={[{ required: true, message: '请选择排序类别' }]}
                    >
                        <Radio.Group optionType="button" buttonStyle="solid" size="middle">
                            <Space wrap style={{ width: '100%' }}>
                                {sortCategories.map(category => (
                                    <Radio.Button key={category.id} value={category.id} style={{ marginBottom: 8 }}>
                                        {category.remark}
                                    </Radio.Button>
                                ))}
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    {/* <Form.Item
                        name="designer"
                        label="图片设计"
                        rules={[{ required: true, message: '请选择图片设计来源' }]}
                        initialValue={0}
                    >
                        <Radio.Group optionType="button" buttonStyle="solid" size="middle">
                            <Space wrap style={{ width: '100%' }}>
                                <Radio.Button value={0} style={{ marginBottom: 8 }}>
                                    外部
                                </Radio.Button>
                                <Radio.Button value={1} style={{ marginBottom: 8 }}>
                                    芯幕
                                </Radio.Button>
                            </Space>
                        </Radio.Group>
                    </Form.Item> */}

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                icon={<UploadOutlined />}
                            >
                                {uploadProgress ? `正在上传 ${uploadProgress.success}/${uploadProgress.total}` : '上传图片'}
                            </Button>
                            <Button
                                onClick={() => {
                                    form.resetFields();
                                    setFileList([]);
                                }}
                                disabled={loading}
                            >
                                重置
                            </Button>
                        </Space>
                        {uploadProgress && (
                            <div style={{ marginTop: 8 }}>
                                <Typography.Text type="secondary">
                                    上传进度：{uploadProgress.success}/{uploadProgress.total}
                                </Typography.Text>
                                <Progress
                                    percent={Math.round(uploadProgress.success / uploadProgress.total * 100)}
                                    size="small"
                                    style={{ marginTop: 4 }}
                                />
                            </div>
                        )}
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default UploadPage; 